import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import logger from "../../util/logger";

import { v4 as uuidv4 } from "uuid";

import Swal from "sweetalert2";

import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../../util/firebase";
import {
  query,
  collection,
  getDocs,
  where,
  doc,
  updateDoc,
  addDoc,
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import BarLoader from "react-spinners/BarLoader";

import { FaChevronLeft } from "react-icons/fa";
import { PiMinusCircleFill, PiPlusCircleFill } from "react-icons/pi";
import { IoClose } from "react-icons/io5";
import { AiOutlineDelete } from "react-icons/ai";

import { Button, Input } from "reactstrap";

function CheckoutPage() {
  const { t } = useTranslation("common");
  // Fetch user data for payment metadata
  const [user] = useAuthState(auth);
  const [userData, setUserData] = useState();
  // Only one userData declaration needed
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.uid) return;
      try {
        const q = query(collection(db, "users"), where("uid", "==", user.uid));
        const docSnap = await getDocs(q);
        if (docSnap.docs.length > 0) {
          setUserData(docSnap.docs[0].data());
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };
    fetchUserData();
  }, [user]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  // Removed duplicate userData declaration
  const [cart, setCart] = useState();
  const [membershipParam, setMembershipParam] = useState();
  const [membershipValidationError, setMembershipValidationError] = useState();

  // Membership product definitions
  const membershipProducts = React.useMemo(
    () => ({
      platinum: {
        itemUid: "membership_platinum",
        itemCategory: "MEMBERSHIP",
        itemName: t("vipPlatinumMembership"),
        itemPrice: 19.99,
        itemQuantity: 1,
        itemImage:
          "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fmemberships%2Fplatinum.png?alt=media&token=2ad2dcac-33cd-4f24-ae4d-99e9f5907b32",
      },
      gold: {
        itemUid: "membership_gold",
        itemCategory: "MEMBERSHIP",
        itemName: t("vipGoldMembership"),
        itemPrice: 9.99,
        itemQuantity: 1,
        itemImage:
          "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fmemberships%2Fgold.png?alt=media&token=69fa2c44-78e3-4229-8d81-7f744bfe3dea",
      },
      silver: {
        itemUid: "membership_silver",
        itemCategory: "MEMBERSHIP",
        itemName: t("vipSilverMembership"),
        itemPrice: 5.99,
        itemQuantity: 1,
        itemImage:
          "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fmemberships%2Fsilver.png?alt=media&token=240c0461-9962-47ec-a3ee-ce0b8b966a49",
      },
    }),
    [t]
  );

  // Detect membership param from URL
  // Removed unused fetchUserData call

  useEffect(() => {
    const fetchCartAndInjectMembership = async () => {
      setIsLoading(true);
      const q = query(
        collection(db, "carts"),
        where("ownerUid", "==", user?.uid)
      );
      const docSnap = await getDocs(q);
      let data = docSnap.docs[0]?.data();

      // Check for membership param
      const params = new URLSearchParams(window.location.search);
      const membership = params.get("membership");
      if (membership && membershipProducts[membership]) {
        // If not already in cart, add membership product
        const alreadyInCart = data?.items?.some(
          (item) => item.itemUid === membershipProducts[membership].itemUid
        );
        if (!alreadyInCart) {
          data = data || { items: [] };
          data.items = [membershipProducts[membership], ...(data.items || [])];
        }
      }

      // Check for entry purchase params (from League -> payWithCash flow)
      const buy = params.get("buy");
      if (buy === "entry") {
        const roomUid = params.get("roomUid");
        const type = params.get("type") || "casual";
        const duration = params.get("duration") || "monthly";
        const price = parseFloat(params.get("price")) || 0;
        // VIP Exclusive entries must be purchased only by Platinum members
        if (type === "vipExclusive") {
          const userTier = userData?.membershipTier;
          const userExpiry = userData?.membershipExpiryDate;
          const hasActive =
            userExpiry && new Date(userExpiry.seconds * 1000) > new Date();
          if (!(hasActive && userTier === "platinum")) {
            Swal.fire({
              text: "VIP Exclusive tournaments are restricted to VIP Platinum members.",
              icon: "warning",
              confirmButtonColor: "#005EE1",
              confirmButtonText: "OKAY",
            });
            // Don't inject the entry into the cart
            setCart(data);
            setIsLoading(false);
            return;
          }
        }
        const entryItemUid = `entry_${roomUid}_${type}_${duration}`;

        const entryItem = {
          itemUid: entryItemUid,
          itemCategory: "ENTRY",
          itemName: `${(type || "Entry").toUpperCase()} ENTRY (${duration})`,
          itemPrice: price,
          itemQuantity: 1,
          itemImage: null,
          metadata: { roomUid, type, duration },
        };

        const alreadyEntryInCart = data?.items?.some(
          (item) => item.itemUid === entryItemUid
        );
        if (!alreadyEntryInCart) {
          data = data || { items: [] };
          data.items = [entryItem, ...(data.items || [])];
        }
      }
      setCart(data);
      setIsLoading(false);
    };
    if (user) {
      fetchCartAndInjectMembership();
    }
  }, [user, membershipProducts, userData]);

  // Read membership query param once
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const m = params.get("membership");
    if (m) setMembershipParam(m);
  }, []);

  // Validate membership param against user's active membership (userData)
  useEffect(() => {
    if (!membershipParam) return;
    // userData may be undefined briefly
    if (!userData) return;

    const userExpiry = userData.membershipExpiryDate;
    const userTier = userData.membershipTier;
    const hasActive =
      userExpiry && new Date(userExpiry.seconds * 1000) > new Date();
    if (hasActive && userTier && userTier !== membershipParam) {
      setMembershipValidationError(
        t("youHaveActiveMembershipExtendOnly", { tier: userTier })
      );
    } else {
      setMembershipValidationError();
    }
  }, [membershipParam, userData, t]);

  const changeItemQuantity = async (item, modifier) => {
    setIsLoading(true);
    try {
      let newCartItems = cart?.items;
      const index = newCartItems.findIndex(
        (obj) => obj.itemUid === item.itemUid
      );
      if (modifier === "+") {
        newCartItems[index].itemQuantity = newCartItems[index].itemQuantity + 1;
      } else if (modifier === "-") {
        newCartItems[index].itemQuantity = Math.max(
          1,
          newCartItems[index].itemQuantity - 1
        );
      }
      const cartRef = query(
        collection(db, "carts"),
        where("ownerUid", "==", user?.uid)
      );
      const findCarts = await getDocs(cartRef);
      findCarts.forEach(async (cartDoc) => {
        const getCart = doc(db, "carts", cartDoc.id);
        await updateDoc(getCart, {
          items: newCartItems,
        });
      });
      setCart({ ...cart, items: newCartItems });
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const removeItemFromCart = (item) => {
    Swal.fire({
      text: "Are you sure you want to remove this item from your cart?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#005EE1",
      confirmButtonText: "YES, REMOVE",
      cancelButtonText: "NO, CANCEL",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);

        try {
          let newCartItems = cart?.items;

          const index = newCartItems.findIndex(
            (obj) => obj.itemUid === item.itemUid
          );
          newCartItems.splice(index, 1);

          const cartRef = query(
            collection(db, "carts"),
            where("ownerUid", "==", user?.uid)
          );
          const findCarts = await getDocs(cartRef);
          findCarts.forEach(async (cart) => {
            const getCart = doc(db, "carts", cart.id);
            await updateDoc(getCart, {
              items: newCartItems,
            });
          });

          // fetchCart removed (was undefined)

          setIsLoading(false);
        } catch (err) {
          console.error(err);
          setIsLoading(false);
        }
      }
    });
  };

  // eslint-disable-next-line
  const clearCart = async () => {
    setIsLoading(true);

    try {
      const cartRef = query(
        collection(db, "carts"),
        where("ownerUid", "==", user?.uid)
      );
      const findCarts = await getDocs(cartRef);
      findCarts.forEach(async (cart) => {
        const getCart = doc(db, "carts", cart.id);
        await updateDoc(getCart, {
          items: [],
        });
      });

      // fetchCart removed (was undefined)

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const [discountCode, setDiscountCode] = useState();
  const [activeDiscountCode, setActiveDiscountCode] = useState();
  const applyDiscountCode = async () => {
    try {
      setIsLoading(true);

      const q = query(
        collection(db, "discountCodes"),
        where("code", "==", discountCode)
      );
      const doc = await getDocs(q);
      if (doc.docs.length > 0) {
        const data = doc.docs[0].data();

        if (data.beneficiaries.includes(user.uid)) {
          Swal.fire({
            text: "You've already used this discount code",
            icon: "error",
            confirmButtonColor: "#005EE1",
            confirmButtonText: "OKAY",
          });
        } else {
          setActiveDiscountCode(data);
        }
      } else {
        Swal.fire({
          text: "Please introduce a valid discount code",
          icon: "error",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
      }

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      alert("An error occured while fetching discount code");
    }
  };

  const getSubtotal = () => {
    let subtotal = 0;
    cart?.items?.forEach((item) => {
      const itemTotal = item.itemPrice * item.itemQuantity;
      subtotal += itemTotal;
    });

    return subtotal;
  };

  const createOrder = async () => {
    setIsLoading(true);

    const cartItemsForPayment = cart.items.map((item) => {
      return {
        product: {
          name: item.itemName,
          price: item.itemPrice,
        },
        quantity: item.itemQuantity,
      };
    });

    const cartItemsForOrder = cart.items.map((item) => {
      return {
        itemUid: item.itemUid,
        itemCategory: item.itemCategory,
        itemName: item.itemName,
        itemPrice: item.itemPrice,
        itemQuantity: item.itemQuantity,
        itemImage: item.itemImage,
        bundleProducts: item.bundleProducts ? item.bundleProducts : null,
      };
    });

    const newOrderUid = uuidv4();

    const body = {
      orderName: "Sport League order",
      orderDescription: "Your Sport League order",
      lineItems: cartItemsForPayment,
      // successUrl: `http://localhost:3000/shop/payment-successful/${newOrderUid}`,
      // cancelUrl: `http://localhost:3000/shop/payment-unsuccessful/${newOrderUid}`,
      successUrl: `https://app.sportleague.com/shop/payment-successful/${newOrderUid}`,
      cancelUrl: `https://app.sportleague.com/shop/payment-unsuccessful/${newOrderUid}`,
      metadata: [
        {
          name: "clientId",
          value: userData.username,
        },
      ],
    };

    fetch("https://api-pay.n1co.shop/api/paymentlink/checkout", {
      method: "POST",
      headers: {
        Authorization:
          "Bearer sk_34c05d1b7b74bf5398a84480a37de830a32ae5f62a5da1ddb85415f3e1f42c37",
        // "Authorization": "Bearer sk_9e129ba8ddfea6fc3c530c32ecdbdf4f09e78c834ec06e5fedfae5d451731055",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    })
      // fetch('https://api-pay.n1co.shop/api/paymentlink/checkout', {
      //   method: "POST",
      //   headers: {
      //     "Authorization": "Bearer sk_9e129ba8ddfea6fc3c530c32ecdbdf4f09e78c834ec06e5fedfae5d451731055",
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify(body)
      // })
      .then((response) => response.json())
      .then((data) => {
        logger.debug(data);
        window.open(data.paymentLinkUrl, "_self");
        addDoc(collection(db, "orders"), {
          uid: newOrderUid,
          items: cartItemsForOrder,
          dateTimeCreated: new Date(),
          userUid: user.uid,
          status: "Pending",
          paymentProcessorOrderCode: data.orderCode,
          paymentProcessorOrderId: data.orderId,
          total: getSubtotal().toFixed(2),
        });
        clearCart();
      })
      .catch((error) => console.error(error));

    setIsLoading(false);
    //   cart.items.forEach(async item => {
    //     if (item.itemCategory === 'SPORTS PASS'){
    //       setIsLoading(true);
    //       try {
    //         const userRef = query(collection(db, "users"), where("uid", "==", user?.uid));
    //         const findUsers = await getDocs(userRef);
    //         findUsers.forEach( async (userObject) => {
    //           const getUser = doc(db, 'users', userObject.id);
    //           await updateDoc(getUser, {
    //             isSportsPassHolder: true,
    //             sportsPassExpiryDate: new Date(new Date().setMonth(new Date().getMonth() + parseInt(item.itemName)))
    //           }).then(() => {
    //             Swal.fire({
    //               text: "Welcome to Sports Pass!",
    //                 icon: 'success',
    //               confirmButtonColor: '#005EE1',
    //               confirmButtonText: "OKAY",
    //             });
    //             clearCart();
    //           });
    //         });
    //         navigate(-1);
    //         setIsLoading(false);
    //       } catch (err) {
    //         console.error(err);
    //         setIsLoading(false);
    //       }
    //     }
    //   })
    // }
  };

  if (isLoading)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{
            width: 100,
            marginBottom: "1rem",
          }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="checkoutPage" className="page">
      <h1>
        <FaChevronLeft
          onClick={() => navigate(-1)}
          style={{
            color: "#005EE1",
            width: 35,
            height: 35,
            marginTop: -8,
            marginRight: "0.25rem",
            cursor: "pointer",
          }}
        />
        {t("checkout").toUpperCase()}
      </h1>
      <div id="checkoutPageContainer">
        <div id="checkoutPageItemsContainer">
          {membershipValidationError ? (
            <div
              style={{ margin: "0 0 1rem 0", color: "#c33", textAlign: "left" }}
            >
              {membershipValidationError}
            </div>
          ) : null}
          {cart?.items?.map((item) => {
            return (
              <div className="checkoutPageItem">
                <img
                  alt="itemImage"
                  src={
                    item.itemCategory === "ENTRY"
                      ? "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Favatars%2FSporty's%2Fcart_base_sporty.png?alt=media&token=46b79641-75d8-465d-9f93-188aa14eee53"
                      : item.itemCategory === "MEMBERSHIP"
                      ? item.itemImage && item.itemImage.startsWith("http")
                        ? item.itemImage
                        : "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fitems%2Fvip_platinum.png?alt=media"
                      : item.itemImage
                      ? item.itemImage
                      : "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fitems%2FdefaultItem.png?alt=media&token=868921ca-e482-437a-b6f1-d02cb5a877b3"
                  }
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src =
                      "https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Fitems%2Fvip_default.png?alt=media";
                  }}
                  style={{
                    height: item.itemCategory === "PROFILE BANNERS" ? 65 : 60,
                    width: 60,
                    objectFit: "contain",
                    borderRadius: 12,
                  }}
                />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div>
                    <p className="checkoutPageItemCategory">
                      {item?.itemCategory
                        ?.toLowerCase()
                        ?.replace(/\b\w/g, (match) => match.toUpperCase())}
                    </p>
                    <p className="checkoutPageItemName">
                      {item.itemName}
                      {item.itemCategory === "SPORTS PASS"
                        ? item.itemName === "1"
                          ? " month Sports Pass"
                          : " months Sports Pass"
                        : null}
                    </p>
                    <div className="checkoutPageItemQuantityContainer">
                      <PiMinusCircleFill
                        onClick={() =>
                          item.itemQuantity > 1 &&
                          item.itemCategory !== "SPORTS PASS"
                            ? changeItemQuantity(item, "-")
                            : null
                        }
                        style={{
                          color:
                            item.itemQuantity < 2 ||
                            item.itemCategory === "SPORTS PASS"
                              ? "#005EE199"
                              : null,
                          cursor:
                            item.itemQuantity < 2 ||
                            item.itemCategory === "SPORTS PASS"
                              ? "default"
                              : "pointer",
                        }}
                      />
                      <p>{item.itemQuantity}</p>
                      <PiPlusCircleFill
                        onClick={() =>
                          item.itemCategory !== "BUNDLE"
                            ? changeItemQuantity(item, "+")
                            : null
                        }
                        style={{
                          color:
                            item.itemCategory === "BUNDLE" ? "#005EE199" : null,
                          cursor:
                            item.itemQuantity < 2 ||
                            item.itemCategory === "BUNDLE"
                              ? "default"
                              : "pointer",
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <p className="checkoutPageItemPrice">
                      ${(item.itemPrice * item.itemQuantity).toFixed(2)}
                    </p>
                    <AiOutlineDelete
                      onClick={() => removeItemFromCart(item)}
                      style={{
                        color: "#005EE1",
                        float: "right",
                        width: 25,
                        height: 30,
                        cursor: "pointer",
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div id="checkoutPageSummaryCard">
          <p className="checkoutPageSummaryCardHeader">ORDER SUMMARY</p>
          <div>
            <p className="inputLabel" style={{ textAlign: "left" }}>
              Have a discount code?
            </p>
            <div id="discountCodeInput" style={{ marginBottom: "1rem" }}>
              <Input
                type="text"
                className="authInput"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                placeholder="Your discount code"
                style={{ marginBottom: 0 }}
              />
              <Button
                onClick={applyDiscountCode}
                className="primaryBtn"
                disabled={!discountCode}
              >
                APPLY
              </Button>
            </div>
          </div>
          <div className="checkoutPageSummaryCardRow">
            <p>Subtotal</p>
            <p className="checkoutPageSummaryCardValue">
              ${getSubtotal()?.toFixed(2)}
            </p>
          </div>
          {activeDiscountCode ? (
            <div className="checkoutPageSummaryCardRow">
              <p>
                {activeDiscountCode.code}
                <IoClose
                  onClick={() => setActiveDiscountCode()}
                  style={{
                    color: "#005EE1",
                    width: 20,
                    height: 20,
                    marginTop: -2.5,
                    cursor: "pointer",
                  }}
                />
              </p>
              <p className="checkoutPageSummaryCardValue">
                - $0.00
                {activeDiscountCode.type === "%" ? (
                  <>&nbsp;(-{activeDiscountCode.value}%)</>
                ) : null}
              </p>
            </div>
          ) : null}
          <div className="checkoutPageSummaryCardTotalRow">
            <p>Total</p>
            <p className="checkoutPageSummaryCardValue">
              ${getSubtotal()?.toFixed(2)}
            </p>
          </div>
          <Button
            onClick={createOrder}
            className="primaryBtn"
            disabled={!!membershipValidationError}
            title={
              membershipValidationError ? membershipValidationError : undefined
            }
          >
            PAY NOW
          </Button>
          {/* disable Pay Now if membership param invalid */}
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
